import type { BillingPreview, BillingProfile } from '@commissionsight/sdk';
import { formatPeriodLong } from '@commissionsight/shared';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { type Stripe, loadStripe } from '@stripe/stripe-js';
import { useEffect, useState } from 'react';
import { useAuth } from '../api.js';
import { AiKeySettings } from '../components/AiKeySettings.js';
import { ReferralCard } from '../components/ReferralCard.js';
import { TeamActivity } from '../components/TeamActivity.js';
import { Button, Card, Field, inputClass } from '../components/ui.js';

type FormState = Omit<BillingProfile, 'card' | 'stripeEnabled'>;

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function dueLabel(iso: string | null): string {
  if (!iso) return 'the 1st of next month';
  const [y, m, d] = iso.split('-').map(Number);
  return m ? `${MONTHS[m - 1]} ${d}, ${y}` : iso;
}

const money = (cents: number) =>
  (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/** US states + DC (USPS code → name) for the state datalist. */
const US_STATES: [string, string][] = [
  ['AL', 'Alabama'],
  ['AK', 'Alaska'],
  ['AZ', 'Arizona'],
  ['AR', 'Arkansas'],
  ['CA', 'California'],
  ['CO', 'Colorado'],
  ['CT', 'Connecticut'],
  ['DE', 'Delaware'],
  ['DC', 'District of Columbia'],
  ['FL', 'Florida'],
  ['GA', 'Georgia'],
  ['HI', 'Hawaii'],
  ['ID', 'Idaho'],
  ['IL', 'Illinois'],
  ['IN', 'Indiana'],
  ['IA', 'Iowa'],
  ['KS', 'Kansas'],
  ['KY', 'Kentucky'],
  ['LA', 'Louisiana'],
  ['ME', 'Maine'],
  ['MD', 'Maryland'],
  ['MA', 'Massachusetts'],
  ['MI', 'Michigan'],
  ['MN', 'Minnesota'],
  ['MS', 'Mississippi'],
  ['MO', 'Missouri'],
  ['MT', 'Montana'],
  ['NE', 'Nebraska'],
  ['NV', 'Nevada'],
  ['NH', 'New Hampshire'],
  ['NJ', 'New Jersey'],
  ['NM', 'New Mexico'],
  ['NY', 'New York'],
  ['NC', 'North Carolina'],
  ['ND', 'North Dakota'],
  ['OH', 'Ohio'],
  ['OK', 'Oklahoma'],
  ['OR', 'Oregon'],
  ['PA', 'Pennsylvania'],
  ['RI', 'Rhode Island'],
  ['SC', 'South Carolina'],
  ['SD', 'South Dakota'],
  ['TN', 'Tennessee'],
  ['TX', 'Texas'],
  ['UT', 'Utah'],
  ['VT', 'Vermont'],
  ['VA', 'Virginia'],
  ['WA', 'Washington'],
  ['WV', 'West Virginia'],
  ['WI', 'Wisconsin'],
  ['WY', 'Wyoming'],
];

const EMPTY: FormState = {
  contactName: '',
  companyName: '',
  email: '',
  phone: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'US', // USA-only for now; the country field is hidden in the UI
};

export function Profile() {
  const { client } = useAuth();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [profile, setProfile] = useState<BillingProfile | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addingCard, setAddingCard] = useState(false);
  const [preview, setPreview] = useState<BillingPreview | null>(null);

  const load = () => {
    client
      .billingPreview()
      .then(setPreview)
      .catch(() => setPreview(null));
    return client
      .getBilling()
      .then((b) => {
        setProfile(b);
        setForm({
          contactName: b.contactName ?? '',
          companyName: b.companyName ?? '',
          email: b.email ?? '',
          phone: b.phone ?? '',
          addressLine1: b.addressLine1 ?? '',
          addressLine2: b.addressLine2 ?? '',
          city: b.city ?? '',
          state: b.state ?? '',
          postalCode: b.postalCode ?? '',
          country: b.country ?? 'US',
        });
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load profile'));
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: load once on mount
  useEffect(() => {
    load();
  }, []);

  const set = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setSaved(false);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const b = await client.updateBilling(form);
      setProfile(b);
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white shadow-sm">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M3 7h18v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"
              stroke="currentColor"
              strokeWidth="2"
            />
            <path d="M3 10h18M7 15h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </span>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Profile &amp; billing</h1>
          <p className="text-sm text-neutral-500">
            Your billing contact, payment method, and next invoice.
          </p>
        </div>
      </div>
      {error && <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}

      <Card>
        <h2 className="mb-4 font-semibold">Billing contact</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Full name">
            <input className={inputClass} value={form.contactName} onChange={set('contactName')} />
          </Field>
          <Field label="Company">
            <input className={inputClass} value={form.companyName} onChange={set('companyName')} />
          </Field>
          <Field label="Billing email">
            <input className={inputClass} type="email" value={form.email} onChange={set('email')} />
          </Field>
          <Field label="Phone">
            <input className={inputClass} value={form.phone} onChange={set('phone')} />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Address line 1">
              <input
                className={inputClass}
                value={form.addressLine1}
                onChange={set('addressLine1')}
              />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Address line 2">
              <input
                className={inputClass}
                value={form.addressLine2}
                onChange={set('addressLine2')}
              />
            </Field>
          </div>
          <Field label="City">
            <input className={inputClass} value={form.city} onChange={set('city')} />
          </Field>
          <Field label="State">
            <input
              className={inputClass}
              list="us-states"
              placeholder="Start typing…"
              value={form.state}
              onChange={set('state')}
            />
            <datalist id="us-states">
              {US_STATES.map(([code, name]) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </datalist>
          </Field>
          <Field label="Postal code">
            <input className={inputClass} value={form.postalCode} onChange={set('postalCode')} />
          </Field>
          {/* Country is fixed to the US for now (USA-only); kept in the saved
              payload via the form's default, no visible field. */}
        </div>
        <div className="mt-5 flex items-center gap-3">
          <Button onClick={save} loading={saving}>
            {saving ? 'Saving…' : 'Save details'}
          </Button>
          {saved && <span className="text-sm text-emerald-600">Saved ✓</span>}
        </div>
      </Card>

      <AiKeySettings />

      <Card>
        <h2 className="mb-1 font-semibold">Payment method</h2>
        <p className="mb-4 text-sm text-neutral-500">
          You're billed on your{' '}
          <span className="font-medium">total member count for the month</span> — at{' '}
          <span className="font-medium">cents per member</span> (your exact rate is in the invoice
          below), and members who churn drop off automatically — plus a payment processing fee, in
          arrears, invoiced on the <span className="font-medium">1st of the following month</span>.
          Pay by <span className="font-medium">bank (ACH)</span> to cut card fees on large invoices.
          Cards and bank details are stored securely by Stripe; we never see them.
        </p>

        {preview && preview.members > 0 && (
          <div className="mb-4 overflow-hidden rounded-xl border border-neutral-200 dark:border-neutral-800">
            <div className="flex items-center justify-between border-b border-neutral-200 bg-neutral-50 px-4 py-2 dark:border-neutral-800 dark:bg-neutral-800/50">
              <span className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                Next invoice · {formatPeriodLong(preview.period)}
              </span>
              <span className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-neutral-600 ring-1 ring-neutral-200 dark:bg-neutral-900 dark:text-neutral-300 dark:ring-neutral-700">
                {preview.lastBilledPeriod === preview.period
                  ? 'Invoiced'
                  : `Due ${dueLabel(preview.dueDate)}`}
              </span>
            </div>
            <div className="space-y-1.5 px-4 py-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-neutral-500">
                  {preview.members.toLocaleString()} members × $
                  {(preview.pricePerMemberCents / 100).toFixed(2)}
                  {preview.custom && <span className="ml-1 text-xs text-sky-600">custom</span>}
                </span>
                <span>{money(preview.amountCents)}</span>
              </div>
              <div className="flex items-center justify-between text-neutral-500">
                <span>
                  Processing fee ({preview.method === 'us_bank_account' ? 'ACH' : 'card'})
                </span>
                <span>{preview.surcharge === false ? 'waived' : money(preview.feeCents)}</span>
              </div>
              {((preview as { referralCreditCents?: number }).referralCreditCents ?? 0) > 0 && (
                <div className="flex items-center justify-between text-emerald-600">
                  <span>Referral credit</span>
                  <span>
                    −{money((preview as { referralCreditCents?: number }).referralCreditCents ?? 0)}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between border-t border-neutral-200 pt-2 text-base font-semibold dark:border-neutral-700">
                <span>Total due</span>
                <span>{money(preview.totalCents)}</span>
              </div>
            </div>
            {preview.surcharge !== false &&
              preview.method !== 'us_bank_account' &&
              preview.achSavingsCents > 100 && (
                <p className="mx-4 mb-3 rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300">
                  💡 Pay by <span className="font-medium">bank (ACH)</span> and the fee drops to{' '}
                  {money(preview.feeCents - preview.achSavingsCents)} — saving{' '}
                  <span className="font-medium">{money(preview.achSavingsCents)}</span> on this
                  invoice.
                </p>
              )}
          </div>
        )}

        {profile && !profile.stripeEnabled ? (
          <div className="rounded-lg border border-dashed border-neutral-300 bg-neutral-50 px-4 py-6 text-center text-sm text-neutral-500 dark:border-neutral-700 dark:bg-neutral-900">
            Card payments are being set up and will be available here shortly.
          </div>
        ) : (
          <>
            {profile?.card && !addingCard && (
              <div className="mb-3 flex items-center justify-between rounded-lg border border-neutral-200 px-4 py-3 dark:border-neutral-800">
                <span className="text-sm">
                  <span className="font-medium capitalize">{profile.card.brand}</span> ••••{' '}
                  {profile.card.last4}
                  {profile.paymentMethod === 'us_bank_account' && (
                    <span className="ml-2 rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium text-sky-700">
                      ACH
                    </span>
                  )}
                </span>
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
                  on file
                </span>
              </div>
            )}
            {addingCard ? (
              <AddCard
                onCancel={() => setAddingCard(false)}
                onSaved={() => {
                  setAddingCard(false);
                  load();
                }}
              />
            ) : (
              <Button variant="ghost" onClick={() => setAddingCard(true)}>
                {profile?.card ? 'Replace payment method' : 'Add a payment method (card or bank)'}
              </Button>
            )}
          </>
        )}
      </Card>

      <ReferralCard />

      <TeamActivity />
    </div>
  );
}

/** Loads a SetupIntent + Stripe.js, then mounts the Payment Element. */
function AddCard({ onSaved, onCancel }: { onSaved: () => void; onCancel: () => void }) {
  const { client } = useAuth();
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: init once
  useEffect(() => {
    client
      .createSetupIntent()
      .then((r) => {
        if (!r.publishableKey) {
          setError('Card payments are not fully configured yet.');
          return;
        }
        setStripePromise(loadStripe(r.publishableKey));
        setClientSecret(r.clientSecret);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not start card setup'));
  }, []);

  if (error) return <p className="text-sm text-rose-600">{error}</p>;
  if (!stripePromise || !clientSecret)
    return <p className="text-sm text-neutral-500">Loading secure card form…</p>;

  const dark = document.documentElement.classList.contains('dark');
  return (
    <Elements
      stripe={stripePromise}
      options={{ clientSecret, appearance: { theme: dark ? 'night' : 'stripe' } }}
    >
      <CardForm onSaved={onSaved} onCancel={onCancel} />
    </Elements>
  );
}

function CardForm({ onSaved, onCancel }: { onSaved: () => void; onCancel: () => void }) {
  const { client } = useAuth();
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!stripe || !elements) return;
    setBusy(true);
    setError(null);
    const { error: confirmError, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
    });
    if (confirmError) {
      setError(confirmError.message ?? 'Card could not be saved');
      setBusy(false);
      return;
    }
    const pm = setupIntent?.payment_method;
    const pmId = typeof pm === 'string' ? pm : pm?.id;
    try {
      if (pmId) await client.savePaymentMethod(pmId);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Saved card but failed to record it');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <PaymentElement />
      {error && <p className="text-sm text-rose-600">{error}</p>}
      <div className="flex gap-2">
        <Button onClick={submit} loading={busy} disabled={!stripe}>
          {busy ? 'Saving…' : 'Save card'}
        </Button>
        <Button variant="ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
